import {
  CdkDragDrop,
  DragDropModule,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileDto } from '@api-client';
import {
  BaseInput,
  DataUrlPipe,
  DragDropDirective,
  FileSizePipe,
  Icon,
  IconButton,
} from '@client';

@Component({
  selector: 'app-select-file',
  imports: [
    CommonModule,
    Icon,
    DataUrlPipe,
    FileSizePipe,
    DragDropModule,
    DragDropDirective,
    IconButton,
  ],
  templateUrl: './select-file.component.html',
  styleUrl: './select-file.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectFileComponent,
      multi: true,
    },
  ],
})
export class SelectFileComponent extends BaseInput<(File | FileDto)[]> {
  multiple = input<boolean, string | boolean>(false, {
    transform: booleanAttribute,
  });

  override writeValue(obj: FileDto | FileDto[]): void {
    if (Array.isArray(obj)) {
      this.value.update(() => obj);
    } else {
      this.value.update(() => (obj ? [obj] : []));
    }
  }

  selectFile() {
    if (!this.multiple() && this.value()?.length === 1) {
      return;
    }

    const input = document.createElement('input') as HTMLInputElement;
    input.type = 'file';
    input.multiple = this.multiple();
    input.accept = this.type() === 'image' ? 'image/*' : '*';

    input.addEventListener('change', (ev) => {
      const files = input.files;
      if (files && files.length > 0) {
        this.handleFileChange(
          !this.multiple() ? [files[0]] : Array.from(files),
        );
      }

      input.remove();
    });

    input.click();
  }

  handleFileChange(files: File[]) {
    this.value.update((prev) => {
      const _prev = prev ?? [];
      return !this.multiple() ? [files[0]] : [..._prev, ...files];
    });

    this.onChange(
      !this.multiple()
        ? [this.value()?.[0] as File | FileDto]
        : (this.value() ?? []),
    );
  }

  handleFileDrop(files: File[]) {
    if (!this.multiple() && (this.value()?.length ?? 0) >= 1) {
      return;
    }
    this.handleFileChange(!this.multiple() ? [files[0]] : files);
  }

  handleDragDrop(ev: CdkDragDrop<File, any>) {
    this.value.update((prev) => {
      const _prev = prev ?? [];

      transferArrayItem(_prev, _prev, ev.previousIndex, ev.currentIndex);
      return _prev;
    });
  }

  removeFile(index: number) {
    const toBeDeleted = this.value()?.[index];

    let name: string | null = null;

    if (toBeDeleted instanceof File) {
      name = toBeDeleted.name;
    }

    if (toBeDeleted) {
      this.value.update((prev) => {
        const _prev = prev ?? [];
        _prev.splice(index, 1);
        return _prev;
      });

      this.onChange(this.value() ?? []);
    }
  }

  getFileName(file: File | string | undefined): string {
    if (!file) return '';

    if (typeof file === 'string') {
      return file.split('/').pop() || '';
    }
    return file.name;
  }
}
